import type { ThemeConfig } from 'antd';

/**
 * Enhanced WorkTool Theme with FastGPT-inspired Design
 * - Sophisticated neutral color palette with 50-950 scale
 * - Refined primary colors with proper variants
 * - Enhanced spacing and typography systems
 * - Dark mode support with proper token management
 * - Sophisticated shadows and border radius scales
 */
export const worktoolTheme: ThemeConfig = {
  cssVar: true,
  token: {
    // Enhanced Primary Color System (FastGPT-inspired)
    colorPrimary: '#3370FF',
    colorPrimaryBg: '#F0F4FF',
    colorPrimaryBgHover: '#E6EFFF',
    colorPrimaryBorder: '#B3D1FF',
    colorPrimaryBorderHover: '#8CC8FF',
    colorPrimaryHover: '#4D7FFF',
    colorPrimaryActive: '#1E5AFF',
    colorPrimaryTextHover: '#4D7FFF',
    colorPrimaryText: '#3370FF',
    colorPrimaryTextActive: '#1E5AFF',

    // Info and Link colors aligned with primary
    colorInfo: '#3370FF',
    colorLink: '#3370FF',
    colorLinkHover: '#4D7FFF',
    colorLinkActive: '#1E5AFF',

    // Refined Functional Colors
    colorSuccess: '#00B365',
    colorSuccessBg: '#F0F9F4',
    colorSuccessBorder: '#B3E5CC',
    colorWarning: '#FF8A00',
    colorWarningBg: '#FFF7F0',
    colorWarningBorder: '#FFD1B3',
    colorError: '#FF4757',
    colorErrorBg: '#FFF0F1',
    colorErrorBorder: '#FFB3BA',

    // Neutral Color Scale (FastGPT-inspired 50-950)
    colorBgBase: '#FFFFFF',
    colorBgContainer: '#FFFFFF',
    colorBgElevated: '#FFFFFF',
    colorBgLayout: '#FAFBFC',
    colorBgSpotlight: '#F8F9FA',
    /** Company Intro 背景色 rgb(245,249,255)*/
    colorBgCompanyIntro: '#F5F9FF',
    /** WorkTool 专用：模块浅蓝背景 rgb(243,247,254) */
    colorBgWorktoolSection: '#F3F7FE',
    colorBgMask: 'rgba(0, 0, 0, 0.45)',

    // Text Colors with Enhanced Hierarchy
    colorText: '#1A202C',
    colorTextSecondary: '#4A5568',
    colorTextTertiary: '#718096',
    colorTextQuaternary: '#A0AEC0',
    colorTextDescription: '#718096',
    colorTextHeading: '#1A202C',
    colorTextLabel: '#2D3748',
    colorTextPlaceholder: '#A0AEC0',
    colorTextDisabled: '#CBD5E0',

    // Border Colors
    colorBorder: '#E2E8F0',
    colorBorderSecondary: '#EDF2F7',

    // Enhanced Spacing System (using standard Ant Design tokens)
    marginXS: 4,
    marginSM: 8,
    margin: 12,
    marginMD: 16,
    marginLG: 24,
    marginXL: 32,
    marginXXL: 48,

    // Sophisticated Border Radius Scale
    borderRadius: 8,
    borderRadiusXS: 4,
    borderRadiusSM: 6,
    borderRadiusLG: 12,
    borderRadiusOuter: 16,

    // Enhanced Shadow System
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    boxShadowSecondary:
      '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
    boxShadowTertiary:
      '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',

    // Typography System
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    fontFamilyCode:
      '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace',

    // Font Sizes (bumped up one level globally)
    fontSizeHeading1: 36,
    fontSizeHeading2: 28,
    fontSizeHeading3: 22,
    fontSizeHeading4: 18,
    fontSizeHeading5: 16,
    fontSize: 16,
    fontSizeSM: 14,
    fontSizeLG: 18,
    fontSizeXL: 22,

    // Font Weights
    fontWeightStrong: 600,

    // Line Heights (increase overall paragraph spacing)
    lineHeight: 1.8,
    lineHeightLG: 1.7,
    lineHeightSM: 1.9,

    // Control Heights
    controlHeight: 36,
    controlHeightSM: 28,
    controlHeightLG: 44,
    controlHeightXS: 24,

    // Motion
    motionDurationFast: '0.1s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',
    motionEaseInOut: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
    motionEaseOut: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
  } as any,
  components: {
    Card: {
      paddingSM: 16,
      padding: 24,
      paddingLG: 32,
      borderRadiusLG: 12,
      boxShadowTertiary:
        '0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)',
    },

    Button: {
      borderRadius: 8,
      borderRadiusLG: 10,
      borderRadiusSM: 6,
      controlHeight: 36,
      controlHeightLG: 44,
      controlHeightSM: 28,
      fontWeight: 500,
      paddingInline: 16,
      paddingInlineLG: 20,
      paddingInlineSM: 12,
    },
    Input: {
      borderRadius: 8,
      controlHeight: 36,
      controlHeightLG: 44,
      controlHeightSM: 28,
      paddingInline: 12,
    },
    Select: {
      borderRadius: 8,
      controlHeight: 36,
      controlHeightLG: 44,
      controlHeightSM: 28,
    },
    Typography: {
      titleMarginBottom: '0.5em',
      titleMarginTop: '1.2em',
    },
    Divider: {
      marginLG: 24,
      margin: 16,
    },
    Space: {
      size: 12,
      sizeLG: 24,
      sizeSM: 8,
    },
  },
};

/** FastGPT-inspired Neutral Color Scale */
export const neutralColors = {
  50: '#FAFBFC',
  100: '#F4F6F8',
  200: '#E4E7EB',
  300: '#D1D5DB',
  400: '#9CA3AF',
  500: '#6B7280',
  600: '#4B5563',
  700: '#374151',
  800: '#1F2937',
  900: '#111827',
  950: '#0A0A0B',
};

/** Dark Mode Color Tokens */
export const darkModeTokens = {
  colorBgBase: '#0A0A0B',
  colorBgContainer: '#111827',
  colorBgElevated: '#1F2937',
  colorBgLayout: '#0A0A0B',
  colorBgSpotlight: '#111827',
  colorText: '#F9FAFB',
  colorTextSecondary: '#D1D5DB',
  colorTextTertiary: '#9CA3AF',
  colorTextQuaternary: '#6B7280',
  colorBorder: '#374151',
  colorBorderSecondary: '#1F2937',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2)',
  boxShadowSecondary:
    '0 4px 6px rgba(0, 0, 0, 0.2), 0 2px 4px rgba(0, 0, 0, 0.15)',
  boxShadowTertiary:
    '0 10px 15px rgba(0, 0, 0, 0.3), 0 4px 6px rgba(0, 0, 0, 0.2)',
};

/** Enhanced Gradients and Decorative Styles */
export const worktoolDecorations = {
  // Primary gradients with refined colors
  primaryGradient: 'linear-gradient(135deg, #3370FF 0%, #4D7FFF 100%)',
  primaryGradientHover: 'linear-gradient(135deg, #4D7FFF 0%, #6699FF 100%)',

  // Subtle background gradients
  softGradient:
    'linear-gradient(180deg, rgba(51, 112, 255, 0.04) 0%, rgba(51, 112, 255, 0.00) 100%)',
  neutralGradient: 'linear-gradient(180deg, #FAFBFC 0%, #F4F6F8 100%)',

  // Company Intro 专用：由 #F5F9FF 渐变到白色（上下垂直）
  companyIntroGradient:
    'linear-gradient(180deg, #F5F9FF 0%,rgb(250,251,252) 100%)',

  // Dark mode gradients
  darkGradient: 'linear-gradient(135deg, #0A0A0B 0%, #111827 100%)',
  darkSoftGradient:
    'linear-gradient(180deg, rgba(51, 112, 255, 0.08) 0%, rgba(51, 112, 255, 0.00) 100%)',

  // Enhanced shadows
  heroGlowShadow: '0 20px 56px rgba(51, 112, 255, 0.24)',
  cardHoverShadow:
    '0 8px 25px rgba(0, 0, 0, 0.08), 0 3px 10px rgba(0, 0, 0, 0.04)',
  elevatedShadow:
    '0 12px 40px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',

  // Interactive states
  hoverOverlay: 'rgba(51, 112, 255, 0.04)',
  activeOverlay: 'rgba(51, 112, 255, 0.08)',
  focusRing: '0 0 0 3px rgba(51, 112, 255, 0.12)',
};

export default worktoolTheme;
